/* eslint-disable camelcase */
import {
  lensPath,
  set,
  ascend,
  prop,
  sortWith,
  path,
  isNil,
  identity,
  isEmpty,
  view,
  pickBy,
  mergeDeepRight,
  propOr,
  compose,
  find,
  pipe,
  keys,
  groupBy,
} from 'ramda';

import {
  GAME_ID_TO_SLUGS_MAP,
  GAME_SLUGS,
  GROUP_SLUGS_TO_TITLE_MAP,
} from '@ezugi/bootstrap/src/constants/game';
import {
  DEVICE,
  AMOUNT_TYPES,
  UPDATED_TABLE_HISTORY,
  UPDATE_FREE_SEATS,
  TABLE_OCCUPANCY,
  SHUFFLE,
} from '@ezugi/constants';
import { formatAmount, DeviceInfo, api } from '@ezugi/utils';

import {
  MINILOBBY_SUBMESSAGE_TYPES,
  BJ_SEATS_NUMBER,
  BJ_NEW_TABLE_DEFAULT_SEATS,
  MINILOBBY_PLAYERS_NUMBER_UPDATE,
} from '../../../constants/minilobby';
import { getMinMaxLimits } from '../../../util/getMinMaxLimits';
import { POST_MESSAGES, postMessage } from '../../../util/postMessage';
import { AuthGroup, LobbyState, LobbyTable } from '../../reducers/lobby';
import {
  definitionsSelector,
  formatSelector,
  tableNamesLocaleSelector,
  userCurrencySelector,
} from '../../selectors';
import { lobbyAllTablesSelector } from '../../selectors/lobby';
import { RootState } from '../../types';
import { POSTMSG_GAMEID_TO_SLUG_MAP, BUNDLE_GAME_MAP } from './constants';

/**
 * socket params and url
 */
const {
  TYPE: { TABLET, SMARTPHONE, HTML },
} = DEVICE;

const VITE_API_URL = import.meta.env.VITE_API_URL ?? '';
const VITE_API_SET_FAVORITE = import.meta.env.VITE_API_SET_FAVORITE ?? '';

const SET_FAVORITE_URL = `${VITE_API_URL}${VITE_API_SET_FAVORITE}`;

export const ADD_FAVORITE = 'add';
export const REMOVE_FAVORITE = 'remove';
type TableType = {
  gameId: keyof typeof POSTMSG_GAMEID_TO_SLUG_MAP;
  title: string;
  id: number;
};

type PostOpenTable = Record<string, any> & Record<'table', TableType>;

const getGameTitleByGameId = (
  gameId: keyof typeof POSTMSG_GAMEID_TO_SLUG_MAP
) => {
  const slug = POSTMSG_GAMEID_TO_SLUG_MAP[gameId];
  if (slug in GROUP_SLUGS_TO_TITLE_MAP) {
    const title = GROUP_SLUGS_TO_TITLE_MAP[slug as keyof typeof GROUP_SLUGS_TO_TITLE_MAP];
    return title;
  }
  return undefined;
};

export const getClientType = () => (DeviceInfo.isTablet && TABLET)
  || (DeviceInfo.isMobile && SMARTPHONE)
  || HTML;

const getUiTableName = (
  { id, title }: { id: number; title: string },
  state: RootState // rarely used legacy code from lobby
) => compose(prop(`tableId_${id}`), tableNamesLocaleSelector)(state) || title;

const postOpenTable = (payload: PostOpenTable, state: RootState) => new Promise((resolve, reject) => {
  const { msg, table, limit, seatIndex } = payload;
  const data = {
    msg,
    msg_type: POST_MESSAGES.OPEN_TABLE,
    event: POST_MESSAGES.OPEN_TABLE,
    game_id: table.gameId,
    game_name: getGameTitleByGameId(table.gameId) || table.title,
    table_id: table.id,
    limit_id: limit.id,
    ...{ uiGameName: getUiTableName(table, state) },
    ...{ seatIndex },
  };

  try {
    postMessage(data);
    resolve(true);
  } catch (e) {
    reject(e);
  }
});

export const getLobbyPostFn = ({ msg }: { msg: string }) => {
  const noOp = () => Promise.reject(new Error('Unknown lobby post message'));
  if (isNil(msg)) return noOp;

  switch (msg) {
  case POST_MESSAGES.OPEN_TABLE:
    return postOpenTable;
  default:
    return noOp;
  }
};

export const pluckSocketParams = (state: RootState) => {
  const {
    config: { definitions, general },
    user,
  } = state;
  const url = definitions?.SERVER_URL_LOBBY;
  const ClientId = `${user.operatorId}|${user.uid}|lobby`;

  const params = {
    ClientIp: user.clientIp,
    ClientId,
    GameID: 0,
    Language: general.language,
    MessageType: 'LobbyInitializeDataByHome',
    Nickname: user.nickname,
    OperatorID: user.operatorId,
    UID: user.uid,
    SessionCurrency: user.currency,
    clientType: getClientType(),
    currentPlayerToken: user.currentPlayerToken,
  };

  return {
    url,
    params,
  };
};

/**
 * tables data
 */
type Game = {
  TableID: number;
  GameTypeID: string;
  TableName: string;
  IsActive: string;
  PlayersNumber: number;
  LimitID: number;
  CurrencyCode: string;
  AvailableSeats: number;
  free_seats: number;
  Min_Bet: number;
  Max_Bet: number;
  Language?: string;
  UseDynamicTableImg?: boolean;
  DynamicTableImg?: string;
  MetaTableID?: number;
  OpenTableImg?: string;
  limits?: any;
};

const sortByMinBet: (a: any[]) => any[] = sortWith([ ascend(prop('minBet')) ]);
const getTables = ({ format, games = [], WidgetData, definitions }: any) => games
  .reduce((acc: Record<string, any>, game: Game) => {
    const {
      TableID,
      GameTypeID,
      TableName,
      IsActive,
      PlayersNumber,
      CurrencyCode,
      AvailableSeats: AvailableGameSeats,
      free_seats,
      MetaTableID,
      UseDynamicTableImg = false,
      DynamicTableImg = `${MetaTableID}.jpg?v=${Date.now()}`,
      OpenTableImg,
      Language,
    } = game;
    const active = IsActive === '1';

    if (!active) return acc;

    const tableWidget = WidgetData.find(
      ({ TableId }: { TableId: number }) => TableId === TableID
    );

    const { AvailableSeatsNumber, AvailableSeats, TableOccupied, History } = tableWidget ?? {};

    const existing = acc[TableID];
    let history: unknown[] | undefined = History;

    if (MetaTableID && TableID !== MetaTableID) {
      // Use history from meta table
      const metaTableWidget = WidgetData.find(
        ({ TableId }: { TableId: number | string }) => +TableId === +MetaTableID
      );

      history = metaTableWidget?.History;
    }

    const useDynamic = Boolean(+UseDynamicTableImg);

    acc[TableID] = {
      ...existing,
      id: TableID,
      gameId: Number(GameTypeID),
      title: TableName,
      active,
      image: useDynamic
        ? definitions.DEALER_IMAGES + DynamicTableImg
        : definitions.OPERATOR_ASSETS + OpenTableImg,
      fallbackImage: definitions.OPERATOR_ASSETS + OpenTableImg,
      language: Language ? String(Language).toLowerCase() : undefined,
      playersCount: PlayersNumber,
      freeSeatsCount: AvailableSeatsNumber ?? free_seats,
      limits:
        sortByMinBet(
          game.limits.reduce(
            (limitsAcc: Array<any>, { LimitID, ...limits }: any) => {
              const { min, max } = getMinMaxLimits({
                gameId: GameTypeID,
                limits,
              });
              const formattedMinAmount = formatAmount(
                +min,
                AMOUNT_TYPES.LIMIT,
                format,
                CurrencyCode
              );
              const formattedMaxAmount = formatAmount(
                +max,
                AMOUNT_TYPES.LIMIT,
                format,
                CurrencyCode
              );
              const denomination = game.limits.find(
                (limit: any) => limit.LimitID === LimitID
              ).Denomination;

              const formattedDenomination = denomination
                ? formatAmount(
                  denomination,
                  AMOUNT_TYPES.LIMIT,
                  format,
                  CurrencyCode
                )
                : null;

              return [
                ...limitsAcc,
                {
                  id: `${LimitID}`,
                  currency: CurrencyCode,
                  minBet: +min,
                  maxBet: +max,
                  range:
                    formattedDenomination
                    || `${formattedMinAmount} - ${formattedMaxAmount}`,
                },
              ];
            },
            []
          )
        ).slice(0, 4) || [],
      seats: AvailableSeats ?? AvailableGameSeats,
      isOccupied: TableOccupied ?? false,
      history,
    };

    return acc;
  }, {});

type SocketMessage = {
  MessageType: string;
  updates: any;
  games: Array<{
    TableID: number;
    GameTypeID: string;
    TableName: string;
    IsActive: string;
    PlayersNumber: number;
    LimitID: number;
    CurrencyCode: string;
    AvailableSeats: number;
    free_seats: number;
    Min_Bet: number;
    Max_Bet: number;
  }>;
  WidgetData: Array<{
    TableId: number;
    AvailableSeatsNumber: number;
    AvailableSeats: Array<string>;
    TableOccupied: boolean;
    History: Array<unknown>;
  }>;
};

export const mapTableList = (state: RootState) => (socketMessage: SocketMessage) => {
  const allTables = lobbyAllTablesSelector(state);
  const format = formatSelector(state);
  const definitions = definitionsSelector(state);

  const { games = [], WidgetData = [] } = socketMessage;
  const gamesByType: Record<string, any> = groupBy(prop('GameTypeID'), games);

  return {
    loading: false,
    tables: Object.keys(gamesByType).reduce(
      (acc: Record<string, any>, gameType: string) => {
        const gamesByTable = getTables({
          format,
          allTables,
          games: gamesByType[gameType],
          WidgetData,
          definitions,
        });
        if (!isEmpty(gamesByTable)) {
          acc[gameType] = { tables: gamesByTable };
        }
        return acc;
      },
      {}
    ),
  };
};

/**
 * updates mapping function for Baccarat
 */
const mapBaccaratUpdates = (
  tables: LobbyState['tables'],
  { gameType, tableId, History = [], GameResults = {} }: any
) => {
  if (!path([ gameType, 'tables', tableId ], tables)) return tables;
  const lens = lensPath([ gameType, 'tables', tableId, 'history' ]);

  const nextTables = set(
    lens,
    !isEmpty(GameResults)
      ? [ ...(view(lens, tables) || []), GameResults ]
      : History,
    tables
  );

  return nextTables;
};

/**
 * updates mapping function for AndarBahar, OTTAndarBahar, UAB, Roulette, Bet On Teen Patti, One Day Teen Patti,
 * Lucky 7, 32 Cards (TTC)
 * GameResults - used after table round update
 * History - used on lobby tables initialization
 */
const mapTableHistoryUpdates = (
  tables: LobbyState['tables'],
  { gameType, tableId, History = [], GameResults = {} }: any
) => {
  if (!path([ gameType, 'tables', tableId ], tables)) return tables;

  const tableHistoryLens = lensPath([ gameType, 'tables', tableId, 'history' ]);

  // table round update uses GameResults prop
  const nextTables = set(
    tableHistoryLens,
    !isEmpty(GameResults)
      ? [ ...(view(tableHistoryLens, tables) || []), GameResults ]
      : History,
    tables
  );

  return nextTables;
};

const mapBlackjackUpdates = (
  tables: LobbyState['tables'],
  { gameType, tableId, free_seats, AvailableSeats = [] }: any
) => {
  if (!path([ gameType, 'tables', tableId ], tables)) return tables;

  const tableSeatsLens = lensPath([ gameType, 'tables', tableId, 'seats' ]);
  const freeSeatsCountLens = lensPath([
    gameType,
    'tables',
    tableId,
    'freeSeatsCount',
  ]);

  // table seats update
  let nextTables = set(
    tableSeatsLens,
    !isEmpty(AvailableSeats)
      ? Object.values(
        mergeDeepRight(view(tableSeatsLens, tables) || [], AvailableSeats)
      )
      : AvailableSeats,
    tables
  );

  // table playersCount update: BJ, ABJ uses multiple seats
  nextTables = set(freeSeatsCountLens, free_seats, nextTables);
  return nextTables;
};

const getUpdateProjectionFunction = (gameSlug: string) => {
  if (isNil(gameSlug)) return identity;

  switch (gameSlug) {
  case GAME_SLUGS.BAC:
    return mapBaccaratUpdates;

  case GAME_SLUGS.BJ:
    return mapBlackjackUpdates;

  case GAME_SLUGS.ANDAR:
  case GAME_SLUGS.OTTAB:
  case GAME_SLUGS.UAB:
  case GAME_SLUGS.ROU:
  case GAME_SLUGS.REVROU:
  case GAME_SLUGS.UR:
  case GAME_SLUGS.BOTP:
  case GAME_SLUGS.ODTP:
  case GAME_SLUGS.ODTP_BL:
  case GAME_SLUGS.L7:
  case GAME_SLUGS.TTC:
  case GAME_SLUGS.SICBO:
  case GAME_SLUGS.MAT:
  case GAME_SLUGS.XD:
  case GAME_SLUGS.MR:
  case GAME_SLUGS.UD:
    return mapTableHistoryUpdates;

  default:
    return identity;
  }
};

const mapPlayersNumberUpdates = ({
  gameType,
  seedTables,
  PlayersNumber,
  tableId,
}: any) => {
  const gameTypeId = gameType
    || compose(
      path([ 'tables', tableId, 'gameId' ]),
      find((game) => Object.keys(propOr({}, 'tables', game)).includes(tableId))
    )(Object.values(seedTables));

  const playersCountLens = lensPath([
    gameTypeId,
    'tables',
    tableId,
    'playersCount',
  ]);

  return isNil(gameTypeId)
    ? seedTables
    : set(playersCountLens, PlayersNumber, seedTables);
};

const getUpdatedOccupancyTables = (
  tables: Record<string, LobbyTable>,
  updateObject: Record<string, any>
) => {
  const gameTypeId = compose(
    prop('id'),
    find((game) => Object.keys(propOr({}, 'tables', game)).includes(updateObject.tableId))
  )(Object.values(tables));
  const isOccupiedLens = lensPath([
    gameTypeId,
    'tables',
    updateObject.tableId,
    'isOccupied',
  ]);

  return !isNil(gameTypeId)
    ? set(isOccupiedLens, updateObject.TableOccupied, tables)
    : tables;
};

type Table = {
  tableId: number;
  game_id: string;
  title: string;
  limits: Record<string, any>;
  open_table_image: string;
  languages: Array<string>;
};

export const mapTableUpdates = (state: RootState) => (updates: Array<any>) => {
  const allTables = lobbyAllTablesSelector(state);
  const format = formatSelector(state);
  const currency = userCurrencySelector(state);

  const tables = updates.reduce((acc: typeof allTables, updateObj: any) => {
    const { MessageType, subMessageType, tableId, TableOccupied, TablesList } = updateObj;
    let gameType: string | number = prop('gameType', updateObj);

    // TODO: temporary fix until bug ERD-547 is fixed; remove this when ERD-547 is fixed
    if (isNil(gameType)) {
      const gameIdLens = (gameId: string) => lensPath([ gameId, 'tables', tableId ]);
      gameType = pipe(
        keys,
        find((gameId) => view(gameIdLens(gameId), allTables))
      )(allTables)!;
    }

    let seedTables = { ...acc };
    const table = allTables[tableId];

    // get specific update function for each gameType
    const gameSlug = GAME_ID_TO_SLUGS_MAP[gameType as keyof typeof GAME_ID_TO_SLUGS_MAP];
    const fn = getUpdateProjectionFunction(gameSlug);

    if (MessageType === MINILOBBY_PLAYERS_NUMBER_UPDATE) {
      TablesList.forEach((_table: Table) => {
        seedTables = mapPlayersNumberUpdates({
          gameType,
          seedTables,
          MessageType,
          TableOccupied,
          ..._table,
        });
      });
    }

    switch (subMessageType) {
    case MINILOBBY_SUBMESSAGE_TYPES.INITIALIZE_TABLE:
      if (!table) return seedTables;

      seedTables = set(
        lensPath([ gameType, 'tables' ]),
        {
          ...(view(lensPath([ gameType, 'tables' ]), allTables) || {}),
          ...{
            [tableId]: {
              limits:
                  sortByMinBet(
                    Object.values(table.limits).reduce(
                      (limitsAcc: Array<any>, { id, ...limits }: any) => {
                        const { min, max } = getMinMaxLimits({
                          gameId: path([ tableId, 'gameId' ], allTables),
                          limits,
                        });
                        const formattedMinAmount = formatAmount(
                          +min,
                          AMOUNT_TYPES.LIMIT,
                          format,
                          currency
                        );
                        const formattedMaxAmount = formatAmount(
                          +max,
                          AMOUNT_TYPES.LIMIT,
                          format,
                          currency
                        );
                        return [
                          ...limitsAcc,
                          {
                            id: `${id}`,
                            currency,
                            minBet: +min,
                            maxBet: +max,
                            range: `${formattedMinAmount} - ${formattedMaxAmount}`,
                          },
                        ];
                      },
                      []
                    )
                  ).slice(0, 4) || [],
              id: prop('tableId', updateObj),
              gameId: path([ tableId, 'game_id' ], allTables),
              title: path([ tableId, 'title' ], allTables),
              active: true,
              image: path([ tableId, 'open_table_image' ], allTables),
              fallbackImage: path([ tableId, 'open_table_image' ], allTables),
              playersCount: 0,
              // BJ, ABJ uses multiple seats
              ...([ '1', '10' ].includes(gameType as string)
                ? {
                  seats: BJ_NEW_TABLE_DEFAULT_SEATS,
                  freeSeatsCount: BJ_SEATS_NUMBER,
                }
                : { history: [] }),
              languages: path([ tableId, 'languages' ], allTables),
            },
          },
        },
        allTables
      );
      return seedTables;
    case MINILOBBY_SUBMESSAGE_TYPES.CANCEL_TABLE:
      seedTables = set(
        lensPath([ gameType, 'tables' ]),
        pickBy(
          (_val, key) => key !== tableId,
          view(lensPath([ gameType, 'tables' ]) || {}, allTables)
        ),
        allTables
      );
      return seedTables;
    case UPDATED_TABLE_HISTORY:
      return fn(acc, { ...updateObj, gameType });
    case UPDATE_FREE_SEATS:
      return fn(acc, updateObj);
    case TABLE_OCCUPANCY:
      return getUpdatedOccupancyTables(acc, updateObj);
    case SHUFFLE: {
      seedTables = set(
        lensPath([ gameType, 'tables', tableId, 'history' ]),
        [],
        allTables
      );

      return seedTables;
    }
    default:
      return seedTables;
    }
  }, allTables);

  return {
    tables,
  };
};

/**
 * lobby data mapping
 */
export const mapLobbyData = (state: RootState, message: SocketMessage) => {
  switch (message.MessageType) {
  case 'InitialData':
    return mapTableList(state)(message);
  case 'UPDATED_LOBBY_DATA':
    return mapTableUpdates(state)(message.updates);
  default:
    return message;
  }
};

export function redirectToGame(
  query: string,
  isBundle: boolean,
  gameId: keyof typeof BUNDLE_GAME_MAP,
  pathSegment: string
) {
  if (isBundle) {
    const gameName = BUNDLE_GAME_MAP[gameId];
    window.location.href = `../${gameName}${query}`;
  } else {
    const pathname = pathSegment
      ? `${pathSegment}/`
      : window.location.pathname.replace(/\/$/, '');
    window.location.href = `${window.location.origin}${pathname}${query}`;
  }
}

// eslint-disable-next-line max-len
export const setFavorite = (params: Record<string, string>) => api.get(`${SET_FAVORITE_URL}?${new URLSearchParams(params).toString()}`);

export const updateAuthGroups = (
  authGroups: Record<string, AuthGroup>,
  tableId: number,
  favorite: boolean
) => {
  const favoriteAuthGroup = authGroups[0];

  if (favorite) {
    return {
      ...authGroups,
      0: {
        ...favoriteAuthGroup,
        tables: favoriteAuthGroup.tables.filter((id) => tableId !== id),
      },
    };
  }

  return {
    ...authGroups,
    0: {
      ...favoriteAuthGroup,
      tables: [ ...favoriteAuthGroup.tables, tableId ],
    },
  };
};
